import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, RefreshCw, Cloud, Mail, CheckCircle2, XCircle, Send, Zap } from "lucide-react";
import { toast } from "sonner";
import {
  getAdminSettings,
  testDynamicsConnection,
  testEmailSend,
  listLeads,
  bulkSyncToDynamics,
} from "@/lib/adminApi";

function StatusPill({ ok, labelOn = "Enabled", labelOff = "Disabled" }) {
  const Icon = ok ? CheckCircle2 : XCircle;
  const cls = ok
    ? "bg-emerald-50 text-emerald-800 border-emerald-200"
    : "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${cls}`}
      data-testid={`settings-pill-${ok ? "on" : "off"}`}
    >
      <Icon size={11} /> {ok ? labelOn : labelOff}
    </span>
  );
}

function DetailRow({ label, value, mono = false }) {
  return (
    <div className="flex items-start justify-between gap-4 border-t border-ra-line/60 py-2.5 text-sm first:border-t-0">
      <dt className="text-ra-ink/55">{label}</dt>
      <dd className={`text-right text-ra-ink ${mono ? "font-mono text-[12px]" : ""}`}>{value || "—"}</dd>
    </div>
  );
}

function CountTile({ label, value, tone = "default", testid }) {
  const tones = {
    default: "bg-ra-paper text-ra-ink",
    success: "bg-emerald-50 text-emerald-900",
    warn: "bg-amber-50 text-amber-900",
    error: "bg-rose-50 text-rose-900",
  };
  return (
    <div
      className={`rounded-2xl border border-ra-line px-4 py-3 ${tones[tone]}`}
      data-testid={testid}
    >
      <p className="text-[10px] font-semibold uppercase tracking-wider opacity-70">{label}</p>
      <p className="mt-1 font-display text-2xl font-semibold">{value}</p>
    </div>
  );
}

export default function AdminSettings() {
  useEffect(() => { document.title = "Settings · Admin · RentAnything.ai"; }, []);

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const [testingDyn, setTestingDyn] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);

  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const s = await getAdminSettings();
      setSettings(s);
    } catch {
      toast.error("Couldn't load settings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [load]);

  const onTestDynamics = async () => {
    setTestingDyn(true);
    try {
      const res = await testDynamicsConnection();
      if (res.ok) toast.success(`Dynamics OK · org ${String(res.organization_id || "").slice(0, 8)}…`);
      else toast.error(`Dynamics test failed: ${res.error || "unknown"}`);
    } catch (e) {
      toast.error(`Dynamics test error: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setTestingDyn(false);
    }
  };

  const onTestEmail = async () => {
    setTestingEmail(true);
    try {
      const res = await testEmailSend();
      if (res.ok) toast.success(`SendGrid OK · ${res.status_code} → ${res.to}`);
      else toast.error(`SendGrid test failed: ${res.error || "unknown"}`);
    } catch (e) {
      toast.error(`SendGrid test error: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setTestingEmail(false);
    }
  };

  const onBulkResync = async () => {
    setBulkRunning(true);
    setBulkResult(null);
    try {
      // 1) gather all pending lead ids (up to backend cap of 200)
      const pendingRes = await listLeads({ dynamics_status: "pending", limit: 200, page: 1 });
      const ids = (pendingRes.items || []).map((l) => l.id);
      // 2) also include failed leads (still recoverable, max 5 attempts in retry loop)
      const failedRes = await listLeads({ dynamics_status: "failed", limit: 200, page: 1 });
      const failedIds = (failedRes.items || []).map((l) => l.id);
      const allIds = Array.from(new Set([...ids, ...failedIds]));

      if (allIds.length === 0) {
        toast.info("No pending or failed leads to re-sync.");
        setBulkRunning(false);
        return;
      }

      const res = await bulkSyncToDynamics(allIds);
      setBulkResult({ queued: res.queued || 0, total: allIds.length, started_at: new Date().toISOString() });
      toast.success(`Bulk re-sync queued (${res.queued || 0} leads). Polling status…`);

      // 3) poll settings every 5s for ~30s
      let ticks = 0;
      pollRef.current = setInterval(async () => {
        ticks += 1;
        try {
          const s = await getAdminSettings();
          setSettings(s);
        } catch { /* ignore */ }
        if (ticks >= 6) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setBulkRunning(false);
          toast.success("Bulk re-sync polling complete. Numbers reflect current state.");
        }
      }, 5000);
    } catch (e) {
      toast.error(`Bulk re-sync failed: ${e?.response?.data?.detail || e.message}`);
      setBulkRunning(false);
    }
  };

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center py-20 text-ra-ink/55" data-testid="admin-settings-loading">
        <Loader2 size={18} className="animate-spin" />
      </div>
    );
  }

  const d = settings.dynamics || {};
  const e = settings.email || {};
  const counts = d.counts || { synced: 0, pending: 0, failed: 0 };

  return (
    <div data-testid="admin-settings">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-ra-ocean">Settings</p>
          <h1 className="mt-1 font-display text-2xl sm:text-3xl font-semibold text-ra-ink">
            Integrations & system status
          </h1>
          <p className="mt-2 text-sm text-ra-ink/65 max-w-xl">
            Read-only view of Dynamics 365 and SendGrid integrations. Credentials are sourced from <code className="text-[12px]">.env</code> and masked — never exposed in the UI.
          </p>
        </div>
        <button
          type="button"
          onClick={() => { setLoading(true); load(); }}
          className="ra-btn-ghost"
          data-testid="admin-settings-refresh"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        {/* Dynamics card */}
        <section
          className="rounded-2xl border border-ra-line bg-white p-6 shadow-card"
          data-testid="admin-settings-dynamics-card"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-ra-ocean/15 text-ra-oceanDark">
                <Cloud size={18} />
              </span>
              <div>
                <h2 className="font-display text-lg font-semibold text-ra-ink">Dynamics 365</h2>
                <p className="text-xs text-ra-ink/55">One-way lead sync · Dataverse v9.2</p>
              </div>
            </div>
            <StatusPill ok={!!d.enabled} />
          </div>

          <dl className="mt-5">
            <DetailRow label="Environment URL" value={d.env_url || "—"} mono />
            <DetailRow label="Client ID" value={d.client_id_masked || "—"} mono />
            <DetailRow label="Vertical field" value="cr463_new_vertical" mono />
          </dl>

          <div className="mt-5 grid grid-cols-3 gap-2.5">
            <CountTile label="Synced"  value={counts.synced  || 0} tone="success" testid="admin-settings-count-synced" />
            <CountTile label="Pending" value={counts.pending || 0} tone="warn"    testid="admin-settings-count-pending" />
            <CountTile label="Failed"  value={counts.failed  || 0} tone="error"   testid="admin-settings-count-failed" />
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onTestDynamics}
              disabled={!d.enabled || testingDyn}
              className="ra-btn-primary"
              data-testid="admin-settings-test-dynamics"
            >
              {testingDyn ? <><Loader2 size={14} className="animate-spin" /> Testing…</> : <><Zap size={14} /> Test connection</>}
            </button>
          </div>
        </section>

        {/* SendGrid card */}
        <section
          className="rounded-2xl border border-ra-line bg-white p-6 shadow-card"
          data-testid="admin-settings-email-card"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-800">
                <Mail size={18} />
              </span>
              <div>
                <h2 className="font-display text-lg font-semibold text-ra-ink">SendGrid email</h2>
                <p className="text-xs text-ra-ink/55">Lead notification pipeline</p>
              </div>
            </div>
            <StatusPill ok={!!e.enabled} />
          </div>

          <dl className="mt-5">
            <DetailRow label="From"   value={e.from_email ? `${e.from_name || ""} <${e.from_email}>` : "—"} />
            <DetailRow label="To"     value={e.to || "—"} />
            <DetailRow label="API key" value={<StatusPill ok={!!e.api_key_set} labelOn="Set" labelOff="Missing" />} />
          </dl>

          <div className="mt-5 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onTestEmail}
              disabled={!e.enabled || testingEmail}
              className="ra-btn-primary"
              data-testid="admin-settings-test-email"
            >
              {testingEmail ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <><Send size={14} /> Send test email</>}
            </button>
          </div>
        </section>
      </div>

      {/* Bulk re-sync */}
      <section
        className="mt-6 rounded-2xl border border-ra-line bg-white p-6 shadow-card"
        data-testid="admin-settings-bulk-sync-card"
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-semibold text-ra-ink">Bulk re-sync to Dynamics</h2>
            <p className="mt-1 text-sm text-ra-ink/65 max-w-2xl">
              Queues every <strong>pending</strong> and <strong>failed</strong> lead for a fresh one-way push to Dynamics. Each lead is rate-limited with a small jitter to stay friendly with Dataverse throttling. Counts above will live-update while the job runs.
            </p>
          </div>
          <button
            type="button"
            onClick={onBulkResync}
            disabled={!d.enabled || bulkRunning}
            className="ra-btn-primary"
            data-testid="admin-settings-bulk-sync"
          >
            {bulkRunning ? <><Loader2 size={14} className="animate-spin" /> Syncing…</> : <><Cloud size={14} /> Re-sync pending & failed</>}
          </button>
        </div>

        {bulkResult && (
          <div className="mt-4 rounded-xl border border-ra-line bg-ra-paper px-4 py-3 text-sm text-ra-ink/75" data-testid="admin-settings-bulk-sync-result">
            Queued <strong className="text-ra-ink">{bulkResult.queued}</strong> of <strong className="text-ra-ink">{bulkResult.total}</strong> lead{bulkResult.total === 1 ? "" : "s"} at {new Date(bulkResult.started_at).toLocaleTimeString()}. Watching the Pending / Synced counts above — refresh once it settles.
          </div>
        )}

        {!d.enabled && (
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Dynamics integration is disabled (<code>DYNAMICS_ENABLED=false</code>). Enable it in <code>backend/.env</code> and restart the backend to use bulk re-sync.
          </div>
        )}
      </section>
    </div>
  );
}
